import { Input, Radio, Typography } from '@material-tailwind/react'
import { useFormikContext } from 'formik'
import { useCallback, useEffect, useRef, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CustomSelect from '@/shared/components/custom-select'
import { ContractSearcher } from '@/components/contract-searcher'
import { getBase64 } from '@/utils/files'
import { ContractSearcherContext } from '@/components/contract-searcher/context'
import { contractTypeOptions } from '@/constants/contract-type-options'
import { EmployeeRoleSelector } from '@/shared/selectors'
import { InputHelperText } from '@/shared/components/input-helper-text'
import PropTypes from 'prop-types'
import { store } from '@/store'
import { employeeRoleApi } from '@/store/services'

export const ContractInformationForm = ({ employee }) => {
  const { t } = useTranslation()
  const {
    values,
    setFieldValue,
    handleChange,
    errors,
    touched,
    submitCount,
    setFieldTouched
  } = useFormikContext()
  const contractInputRef = useRef(null)
  const { currentContract } = useContext(ContractSearcherContext)
  const previousContractIdRef = useRef()
  const [loadingState, setLoadingState] = useState({})
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false)

  const setLoading = useCallback((key, isLoading) => {
    setLoadingState((currentState) => ({
      ...currentState,
      [key]: isLoading
    }))
  }, [])

  const isLoadingFormData = useMemo(
    () => Object.values(loadingState).some(Boolean),
    [loadingState]
  )

  const contractorError = errors.employee_contracts_attributes?.contractor_contract_id
  const shouldShowError =
    (touched.employee_contracts_attributes?.contractor_contract_id || submitCount > 0) &&
    contractorError

  const contractType = values.employee_contracts_attributes?.contract_type
  const isFixedTerm = contractType === 'fixed_term'
  const selectedContractId = values.employee_contracts_attributes?.contractor_contract_id
  const selectedEmployeeRoleId = values.employee_contracts_attributes?.employee_role_id
  const contractServiceAreaIds = useMemo(() => {
    const serviceAreas = currentContract?.serviceAreas || currentContract?.service_areas || []

    return serviceAreas
      .map((serviceArea) => Number(serviceArea.id))
      .filter(Boolean)
  }, [currentContract])

  useEffect(() => {
    if (!isLoadingFormData) {
      setShowLoadingOverlay(false)
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      setShowLoadingOverlay(true)
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [isLoadingFormData])

  useEffect(() => {
    if (previousContractIdRef.current === undefined) {
      previousContractIdRef.current = selectedContractId || null
      return
    }

    if (!selectedContractId) {
      previousContractIdRef.current = null
      setFieldValue('employee_contracts_attributes.employee_role_id', null)
      return
    }

    const contractChanged = String(previousContractIdRef.current) !== String(selectedContractId)
    if (!contractChanged) return
    if (String(currentContract?.id) !== String(selectedContractId)) return

    previousContractIdRef.current = selectedContractId

    if (!selectedEmployeeRoleId) return
    if (!contractServiceAreaIds.length) return

    setLoading('roleValidation', true)

    const searchParams = new URLSearchParams({
      page: 1,
      per_page: 1,
      employee_role_id: selectedEmployeeRoleId
    })
    contractServiceAreaIds.forEach((id) => searchParams.append('service_area_ids[]', id))

    store.dispatch(employeeRoleApi.endpoints.getEmployeeRoles.initiate(`?${searchParams}`))
      .unwrap()
      .then(({ employeeRoles = [] }) => {
        const roleMatchesContract = employeeRoles.some((employeeRole) =>
          Number(employeeRole.id) === Number(selectedEmployeeRoleId)
        )

        if (!roleMatchesContract) {
          setFieldValue('employee_contracts_attributes.employee_role_id', null)
        }
      })
      .finally(() => setLoading('roleValidation', false))
  }, [selectedContractId, selectedEmployeeRoleId, currentContract, contractServiceAreaIds])

  const uploadContract = (event) => {
    const file = event.target.files[0]
    getBase64(file, async (base64File) => {
      setFieldValue('employee_contracts_attributes.document', {
        data: base64File,
        filename: file.name,
        content_type: file.type,
        identify: false
      })
    })
  }

  useEffect(() => {
    if (!employee?.employeeContracts?.length) return
    if (!employee?.employeeContracts[0]?.documentInfo?.filename) return

    if (contractInputRef.current) {
      const contractFile = new File(['asd'], employee.employeeContracts[0].documentInfo.filename, {
        type: employee.employeeContracts[0].documentInfo.content_type
      })
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(contractFile)
      contractInputRef.current.files = dataTransfer.files
      contractInputRef.current.value = 'asdasdasd.jopg'
    }
  }, [employee])

  useEffect(() => {
    if (isFixedTerm) {
      setFieldTouched('employee_contracts_attributes.finish_date', true, false)
    } else {
      setFieldValue('employee_contracts_attributes.finish_date', null, false)
      setFieldTouched('employee_contracts_attributes.finish_date', false, false)
    }
  }, [contractType])

  return (
    <div className='relative grid grid-cols-1 gap-3'>
      {showLoadingOverlay && (
        <div className='fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-white/80'>
          <div className='h-8 w-8 rounded-full border-2 border-blue-600 border-t-transparent animate-spin' />
          <p className='text-sm font-medium text-gray-700'>Cargando datos del contrato...</p>
        </div>
      )}
      <div className='flex flex-col items-end'>
        <Input
          ref={contractInputRef}
          type='file'
          name='employee_contracts_attributes.document'
          label='Contrato de trabajador'
          onChange={uploadContract}
        />
        {currentContract && Object.keys(currentContract).length > 0 && (
          <a
            className='text-blue-400 underline text-sm'
            href={
              employee?.employeeContracts?.find((contract) => contract.active)?.documentInfo?.url ||
              null
            }
            target='_blank'
            rel='noreferrer'
          >
            Ver contrato
          </a>
        )}
      </div>

      <div className='grid grid-cols-2 gap-3'>
        <ContractSearcher
          required
          name='employee_contracts_attributes.contractor_contract_id'
          error={Boolean(
            (touched.employee_contracts_attributes?.contractor_contract_id || submitCount > 0) &&
              errors.employee_contracts_attributes?.contractor_contract_id
          )}
        />

          {shouldShowError && (
            <div className='col-span-2'>
              <InputHelperText label={contractorError} color='red' className='-mt-3' />
            </div>
          )}

        <CustomSelect
          required
          placeholder={'Tipo de contrato'}
          name='employee_contracts_attributes.contract_type'
          onChange={({ value }) =>
            setFieldValue('employee_contracts_attributes.contract_type', value)
          }
          options={contractTypeOptions}
          value={
            contractTypeOptions.find(
              ({ value: contractType }) =>
                contractType === values?.employee_contracts_attributes?.contract_type
            ) || ''
          }
          error={Boolean(
            (touched.employee_contracts_attributes?.contract_type || submitCount > 0) &&
              errors.employee_contracts_attributes?.contract_type
          )}
        />

        <InputHelperText
          label={
            (touched.employee_contracts_attributes?.contract_type || submitCount > 0) &&
            errors.employee_contracts_attributes?.contract_type
          }
          color='red'
        />

        <EmployeeRoleSelector
          required
          disabled={!selectedContractId}
          serviceAreaIds={contractServiceAreaIds}
          onLoadingChange={(isLoading) => setLoading('employeeRole', isLoading)}
          value={values?.employee_contracts_attributes?.employee_role_id}
          onChange={(option = {}) =>
            setFieldValue('employee_contracts_attributes.employee_role_id', option?.value)
          }
          error={Boolean(
            (touched.employee_contracts_attributes?.employee_role_id || submitCount > 0) &&
              errors.employee_contracts_attributes?.employee_role_id
          )}
        />
        <InputHelperText
          label={
            (touched.employee_contracts_attributes?.employee_role_id || submitCount > 0) &&
            errors.employee_contracts_attributes?.employee_role_id
          }
          color='red'
        />

        {/* Fila: Fecha inicio y Fecha término */}
        <div className='grid grid-cols-2 gap-3 col-span-2'>
          <div>
            <Input
              required
              type='date'
              name='employee_contracts_attributes.start_date'
              label='Fecha inicio contrato'
              value={values.employee_contracts_attributes?.start_date || ''}
              onChange={handleChange}
              error={Boolean(
                (touched.employee_contracts_attributes?.start_date || submitCount > 0) &&
                  errors.employee_contracts_attributes?.start_date
              )}
            />
            <InputHelperText
              label={
                (touched.employee_contracts_attributes?.start_date || submitCount > 0) &&
                errors.employee_contracts_attributes?.start_date
              }
              color='red'
            />
          </div>

          <div>
            <Input
              type='date'
              name='employee_contracts_attributes.finish_date'
              disabled={!isFixedTerm}
              label={t('common.expirationDate')}
              value={values.employee_contracts_attributes?.finish_date || ''}
              onChange={handleChange}
              error={Boolean(
                isFixedTerm &&
                  (touched.employee_contracts_attributes?.finish_date || submitCount > 0) &&
                  errors.employee_contracts_attributes?.finish_date
              )}
            />
            {isFixedTerm && (
              <InputHelperText
                label={
                  (touched.employee_contracts_attributes?.finish_date || submitCount > 0) &&
                  errors.employee_contracts_attributes?.finish_date
                }
                color='red'
              />
            )}
          </div>
        </div>

        <div>
          <Input
            type='date'
            name='employee_contracts_attributes.assignation_date'
            label={t('common.assignationDate')}
            value={values.employee_contracts_attributes?.assignation_date || ''}
            onChange={handleChange}
          />
        </div>

        <div>
          <Typography variant='small' className='text-gray-500 font-semibold'>
            {t('employee.contractInformationForm.registerAssitance.question')}
          </Typography>
          <div className='flex gap-4'>
            <Radio
              id='radio-register_attendance-yes'
              value={true}
              name='employee_contracts_attributes.register_attendance'
              label='Si'
              checked={values?.employee_contracts_attributes?.register_attendance}
              onChange={() => {
                setFieldValue('employee_contracts_attributes.register_attendance', true)
              }}
            />
            <Radio
              id='radio-register_attendance-no'
              value={false}
              name='employee_contracts_attributes.register_attendance'
              label='No'
              checked={!values?.employee_contracts_attributes?.register_attendance}
              onChange={() => {
                setFieldValue('employee_contracts_attributes.register_attendance', false)
              }}
            />
          </div>
        </div>

        <div>
          <Typography variant='small' className='text-gray-500 font-semibold'>
            {t('employee.contractInformationForm.workingHours.one')}
          </Typography>
          <Input
            value={values?.employee_contracts_attributes?.working_hours || ''}
            onChange={handleChange}
            name='employee_contracts_attributes.working_hours'
            error={Boolean(
              (touched.employee_contracts_attributes?.working_hours || submitCount > 0) &&
              errors.employee_contracts_attributes?.working_hours
            )}
          />
          <InputHelperText
            color='red'
            label={
              (touched.employee_contracts_attributes?.working_hours || submitCount > 0) &&
              errors.employee_contracts_attributes?.working_hours
            }
          />
        </div>
      </div>
    </div>
  )
}

ContractInformationForm.propTypes = {
  employee: PropTypes.object
}
